<template>
    <div class="work-location-card">
        <div class="work-location-header">
            <div>
                <h3>{{ $t('landing-page.work-location') }}</h3>
                <p class="subtitle">{{ $t('landing-page.work-location-description') }}</p>
            </div>
            <div class="animated-button-container">
                <AnimatedButton />
            </div>
        </div>

        <div class="work-location-body">
            <div class="location-tabs">
                <div class="tab-item" :class="{ active: workLocation === 'inOffice' }"
                    @click="workLocation = 'inOffice'">
                    <i class="mdi mdi-briefcase"></i> {{ $t('landing-page.in-office') }} >
                </div>
                <div class="tab-item" :class="{ active: workLocation === 'remoteWork' }"
                    @click="workLocation = 'remoteWork'">
                    <i class="mdi mdi-home"></i> {{ $t('landing-page.remote-work') }} >
                </div>
            </div>

            <div class="tab-content">
                <!-- In-Office Section -->
                <div v-if="workLocation === 'inOffice'" class="in-office-grid">
                    <div v-for="(card, index) in inOfficeCards" :key="index" class="info-card" :class="card.background">
                        <div class="text-content">
                            <p class="title">{{ card.title }}</p>
                            <p class="desc">{{ card.desc }}</p>
                        </div>
                        <img :src="card.image" class="info-image" />
                    </div>
                </div>

                <!-- Remote Work Section -->
                <div v-else class="remote-work-card">
                    <div class="section-header">{{ $t('landing-page.select-hours') }}</div>

                    <div class="today-date-row">
                        <div class="today-label">{{ $t('booking.today') }}</div>
                        <div class="today-date">{{ moment().format('DD/MM/YYYY') }}</div>
                    </div>

                    <v-row dense class="selects-grid">
                        <v-col cols="12" md="6" sm="6">
                            <v-select v-model="startTime" :items="times" :label="$t('landing-page.work-start')"
                                :disabled="isSaveDisabled" variant="outlined" density="comfortable" rounded
                                class="time-select" />
                        </v-col>

                        <v-col cols="12" md="6" sm="6">
                            <v-select v-model="endTime" :items="times" :label="$t('landing-page.return-home')"
                                :disabled="isSaveDisabled" variant="outlined" density="comfortable" rounded
                                class="time-select" />
                        </v-col>

                        <v-col cols="12" md="6" sm="6">
                            <v-select v-model="lunchDuration" :items="lunchDurations" :disabled="isSaveDisabled"
                                :label="$t('landing-page.lunch-break')" variant="outlined" density="comfortable" rounded
                                class="time-select" />
                        </v-col>
                    </v-row>

                    <div class="save-button-container">
                        <v-btn color="primary" rounded class="save-button" :disabled="isSaveDisabled"
                            @click="saveSchedule">
                            {{ $t('landing-page.save') }}
                        </v-btn>
                        <p v-if="isSaveDisabled" class="save-hint">{{ $t('landing-page.contact-admin') }}</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import moment from 'moment'
import { useI18n } from 'vue-i18n'
import AnimatedButton from '@/components/general/ChildComponents/AnimatedButton.vue'
import snacks from '@/assets/Branding/Pia/snacks.png'
import lunch from '@/assets/Branding/Pia/lunch.png'
import meeting from '@/assets/Branding/Pia/meeting.png'
import fun from '@/assets/Branding/Pia/fun.png'
import { isWebPlatform, getStoredItem, setStoredItem, removeStoredItem } from '@/services/utils.js'

const workLocation = ref('inOffice')
const startTime = ref('09:00')
const endTime = ref('18:00')
const lunchDuration = ref('30 min')
const isSaveDisabled = ref(false)
const currentDate = ref(moment().format('DD/MM/YYYY'))
const { locale, t } = useI18n()

const times = [
    '08:00', '09:00', '10:00', '11:00', '12:00',
    '13:00', '14:00', '15:00', '16:00', '17:00', '18:00'
]

const lunchDurations = ['30 min', '45 min', '1 hour']

const inOfficeCards = computed(() => [
    {
        title: t('landing-page.enjoy-snacks'),
        desc: t('landing-page.snacks-description'),
        image: snacks,
        background: 'card-blue'
    },
    {
        title: t('landing-page.enjoy-lunch'),
        desc: t('landing-page.lunch-description'),
        image: lunch,
        background: 'card-yellow'
    },
    {
        title: t('landing-page.enjoy-break'),
        desc: t('landing-page.break-description'),
        image: meeting,
        background: 'card-lightblue'
    },
    {
        title: t('landing-page.enjoy-events'),
        desc: t('landing-page.events-description'),
        image: fun,
        background: 'card-purple'
    }
])

// Load saved schedule and check if save should be disabled
onMounted(async () => {
    const savedSchedule = await getStoredItem('remoteWorkSchedule')
    if (savedSchedule && savedSchedule.date === currentDate.value) {
        startTime.value = savedSchedule.startTime
        endTime.value = savedSchedule.endTime
        lunchDuration.value = savedSchedule.lunchDuration
        isSaveDisabled.value = true
    }
})

// Watch for date changes
watch(() => moment().format('DD/MM/YYYY'), async (newDate) => {
    if (newDate !== currentDate.value) {
        currentDate.value = newDate
        isSaveDisabled.value = false
        await removeStoredItem('remoteWorkSchedule')
    }
})

// Save schedule
const saveSchedule = async () => {
    const schedule = {
        date: currentDate.value,
        startTime: startTime.value,
        endTime: endTime.value,
        lunchDuration: lunchDuration.value
    }
    await setStoredItem('remoteWorkSchedule', schedule)
    isSaveDisabled.value = true
}

</script>

<style scoped>
/* --- Main Card --- */
.work-location-card {
    background: white;
    border-radius: 14px;
    padding: 30px;
    margin: 0 auto;
    margin-top: 20px;
    position: relative;
    z-index: 10;
    max-width: calc(100vw - 20%);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

/* --- Header --- */
.work-location-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
}

.subtitle {
    color: #718096;
    margin-top: 4px;
    font-size: 17px;
}

.animated-button-container {
    display: flex;
    justify-content: flex-end;
}

/* --- Body --- */
.work-location-body {
    display: flex;
    margin-top: 1rem;
}

.location-tabs {
    width: 180px;
    border-right: 1px solid #e0e0e0;
    display: flex;
    flex-direction: column;
}

.tab-item {
    padding: 12px;
    cursor: pointer;
    font-weight: 500;
    color: #374151;
    transition: background 0.2s ease;
}

.tab-item:hover {
    background-color: #f3f4f6;
}

.tab-item.active {
    color: #2563eb;
    font-weight: bold;
    background-color: #e0f2fe;
    border-right: 4px solid #2563eb;
}

.tab-content {
    flex: 1;
    padding-left: 20px;
}

/* --- In-Office Grid --- */
.in-office-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 14px;
}

.info-card {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px;
    border-radius: 12px;
    overflow: hidden;
    position: relative;
    min-height: 130px;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.info-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.1);
    cursor: pointer;
}

.text-content {
    flex: 1;
    padding-right: 8px;
}

.title {
    font-weight: bold;
    font-size: 16px;
    margin-bottom: 4px;
    color: #111827;
}

.desc {
    font-size: 13px;
    color: #6b7280;
}

.info-image {
    width: 110px;
    height: 90px;
    object-fit: cover;
    clip-path: polygon(40% 0%, 100% 0%, 100% 100%, 0 100%);
}

/* --- Remote Work Section --- */
.remote-work-card {
    background: #f8fafc;
    border-radius: 12px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.section-header {
    font-weight: bold;
    font-size: 16px;
}

.today-date-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #e5e7eb;
    padding: 10px 20px;
    border-radius: 10px;
    font-weight: bold;
    color: #374151;
}

.today-label {
    font-size: 16px;
}

.today-date {
    font-size: 16px;
    color: #3b82f6;
}

.selects-grid {
    margin-top: 10px;
}

.time-select {
    width: 100%;
}

.save-button-container {
    align-self: flex-end;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    margin-top: 10px;
}

.save-button {
    width: 100px;
}

.save-hint {
    font-size: 12px;
    color: #6b7280;
    margin-top: 5px;
    text-align: right;
}

/* Fix select field height */
.v-input {
    min-height: 50px;
}

/* --- Background Colors --- */
.card-blue {
    background: #eff6ff;
}

.card-yellow {
    background: #fef9c3;
}

.card-lightblue {
    background: #e0f2fe;
}

.card-purple {
    background: #f3e8ff;
}

/* --- Media Queries --- */
@media (max-width: 1200px) {
    .info-image {
        width: 100px;
        height: 80px;
    }
}

@media (max-width: 992px) {
    .work-location-body {
        flex-direction: column;
    }

    .location-tabs {
        flex-direction: row;
        width: 100%;
        border-right: none;
        border-bottom: 1px solid #e0e0e0;
        margin-bottom: 10px;
    }

    .tab-item {
        flex: 1;
        text-align: center;
    }

    .tab-content {
        padding-left: 0;
    }

    .in-office-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 850px) {
    .work-location-card {
        max-width: calc(100vw - 10%);
    }

    .info-image {
        width: 90px;
        height: 70px;
    }

    .info-card {
        min-height: 110px;
    }

    .title {
        font-size: 14px;
    }

    .desc {
        font-size: 12px;
    }
}
</style>